import { memo, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';

import { PublicBoardNullState } from '~/components/PublicBoard/PublicBoardNullState';
import { PublicGalleryAsset } from '~/components/v2/components/board/gallery/PublicGalleryAsset';
import { PublicGalleryBoard } from '~/components/v2/components/board/gallery/PublicGalleryBoard';
import { PublicGalleryFile } from '~/components/v2/components/board/gallery/PublicGalleryFile';
import { usePublicBoardGalleryNestedControls } from '~/components/v2/components/board/gallery/usePublicBoardGalleryNestedControls';
import { type PublicBoardItemsViewProps } from '~/components/v2/components/board/PublicBoardContent';
import { GalleryView, type GalleryViewProps } from '~/components/v2/components/shared/gallery/GalleryView';
import { usePublicPermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePublicPermissionedVisibleCustomFieldsLength';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { usePublicGalleryViewItems } from '~/swr-hooks/gallery/galleryView/usePublicGalleryViewItems';

// The public asset/file cards close over nothing, so they live once at module scope; the board
// card is built inside the component because it closes over this tree's drill.
const renderAsset: GalleryViewProps<Board, Clip>['renderAsset'] = ({ box, data, index }) => (
  <PublicGalleryAsset box={box} data={data} index={index} key={data.id} />
);

const renderFile: GalleryViewProps<Board, Clip>['renderFile'] = ({ box, data, index }) => (
  <PublicGalleryFile box={box} data={data} index={index} key={data.id} />
);

export type PublicBoardPageGalleryViewProps = PublicBoardItemsViewProps;

/**
 * The standalone board link's gallery view — this surface's cards over this surface's fetch scope,
 * composed into PublicBoard's GalleryView slot by PublicBoardPage. Renders inside PublicBoard's
 * internal provider, so the board card's drill is the board tree's own contract binding (the URL
 * drill the page supplied).
 */
export const PublicBoardPageGalleryView = memo(({ scrollElementRef }: PublicBoardPageGalleryViewProps) => {
  const { onNavigateToBoard } = usePublicBoard();
  // No collectionId: a standalone board link has no collection — the fetch's one designed absence
  // (collection surfaces always state their scope explicitly).
  const itemsData = usePublicGalleryViewItems();
  const { emptyStateHeader, renderHeader } = usePublicBoardGalleryNestedControls();

  const renderBoard = useMemo<GalleryViewProps<Board, Clip>['renderBoard']>(
    () =>
      ({ box, data, index }) => (
        <PublicGalleryBoard box={box} data={data} index={index} key={data.id} onNavigateToBoard={onNavigateToBoard} />
      ),
    [onNavigateToBoard],
  );

  return (
    <GalleryView
      itemsData={itemsData}
      // The standalone board link's own null state — what an empty board on a board link has always
      // rendered, now stated here instead of inherited from the dissolved PublicGalleryView wrapper.
      emptyStateHeader={emptyStateHeader}
      NullState={PublicBoardNullState}
      renderAsset={renderAsset}
      renderBoard={renderBoard}
      renderFile={renderFile}
      renderHeader={renderHeader}
      scrollElementRef={scrollElementRef}
      // The public permission model's custom-field count — every public gallery states it.
      useCustomFieldsLength={usePublicPermissionedVisibleCustomFieldsLength}
      // This surface mounts ONE gallery, so it registers its own items as the selectable set.
      useSelectableItems={useGroupedGallerySelectedItems}
    />
  );
});

PublicBoardPageGalleryView.displayName = 'PublicBoardPageGalleryView';
